/**
 * Mixin methods that handle realtime chat actions
 */
import { RealTimeAPI } from 'rocket.chat.realtime.api.rxjs'
import { mapGetters, mapActions } from 'vuex'
const emoji = require('node-emoji')

export default {
  name: 'mixin-chatrealtime',
  data () {
    return {
      realTimeAPI: null
    }
  },
  created () {
    document.addEventListener('beforeunload', this.beforeUnload)
  },
  computed: {
    ...mapGetters({
      chatUser: 'chat/authenticatedRocketChatUser'
    }),

    chatUrl () {
      return new URL(this.chatUser?.homeUrl)
    },

    chatWssUrl () {
      return `${this.protocol}://${this.chatUrl?.host}/websocket`
    },

    protocol () {
      return this.chatUrl.protocol === 'https:' ? 'wss' : 'ws'
    },

    authToken () {
      return this.chatUser?.authToken
    }
  },
  watch: {
    chatUser () {
      if (this.realTimeAPI === null) {
        this.openConnection()
      } else {
        this.reLoginRealtimeApi()
      }
    }
  },
  methods: {
    ...mapActions({
      selectRoom: 'chat/selectRoom',
      updateLastSmsConversationMessage: 'chat/updateLastSmsConversationMessage',
      updateLastDirectConversationMessage: 'chat/updateLastDirectConversationMessage',
      updateLastChannelMessage: 'chat/updateLastChannelMessage',
      updateLastGroupMessage: 'chat/updateLastGroupMessage'
    }),

    openConnection () {
      this.realTimeAPI = new RealTimeAPI(this.chatWssUrl)
      this.realTimeAPI.connectToServer()
      this.realTimeAPI.keepAlive().subscribe()
      this.loginRealtimeApi()
    },

    loginRealtimeApi () {
      this.realTimeAPI.loginWithAuthToken(this.authToken).subscribe()
    },

    reLoginRealtimeApi () {
      this.realTimeAPI.loginWithAuthToken(this.authToken)
    },

    /* Request notification of events and act on them */
    subscribeEvent (id, name, params, cb) {
      this.realTimeAPI.sendMessage({ msg: 'sub', id: id, name: name, params: params })
      this.realTimeAPI.subscribe((message) => {
        if (message?.msg !== 'ping') {
          cb(message)
        }
      })
    },

    /* Subscribe to new user notifications */
    onOpenRoom (room, reset) {
      this.selectRoom(room)
      // on first load room
      if (reset) {
        this.messages = []
        this.rocketMessages = []
        this.realTimeAPI.sendMessage({ msg: 'unsub', id: 'stream-room-messages' })
        this.realTimeAPI.sendMessage({ msg: 'unsub', id: 'stream-notify-room' })
        this.realTimeAPI.sendMessage({ msg: 'method', id: 'openRoom', method: 'openRoom', params: [room.roomId] })
        const loadHistoryParams = [room.roomId, null, this.scrollDistance, { $date: Math.floor(Date.now() / 1000) }]
        this.realTimeAPI.sendMessage({ msg: 'method', id: 'loadHistory', method: 'loadHistory', params: loadHistoryParams })
        this.subscribeMessages(room.roomId)
        this.subscribeTyping(room.roomId)
      // on infinite scroll
      } else {
        const scrollToDate = { $date: Math.floor((new Date(this.firstMessage.date))) }
        const loadHistoryParams = [room.roomId, scrollToDate, this.scrollDistance, { $date: Math.floor(Date.now() / 1000) }]
        this.realTimeAPI.sendMessage({ msg: 'method', id: 'loadHistory', method: 'loadHistory', params: loadHistoryParams })
      }
    },

    subscribeMessages (roomId) {
      const that = this
      this.subscribeEvent('stream-room-messages', 'stream-room-messages', [roomId, false], (message) => {
        // init msg history
        if (message.id === 'loadHistory' && message.msg === 'result') {
          const result = message.result
          const roomMessages = result?.messages
          this.messagesLoaded = false
          if (roomMessages && roomMessages.length > 0) {
            const roomMessagesFormatted = roomMessages.map(function (roomMessage) {
              return that.formatMessage(roomMessage)
            })
            if (this.reset) {
              this.messages = roomMessagesFormatted.reverse()
            } else {
              this.messages = [...roomMessagesFormatted.reverse(), ...this.messages]
            }
            this.rocketMessages = roomMessages
            this.messagesLoaded = result.unreadNotLoaded === 0
          } else {
            this.messages = []
            setTimeout(() => {
              this.messagesLoaded = true
            }, 0)
          }
        // msg on send
        } else if (message.msg === 'result' && message.id === 'sendMessage') {
          const roomMessage = message.result
          this.messages = this.messages.filter(Boolean)
          this.setMessageAsSent(roomMessage)
        // msg on receive
        } else if (message.msg === 'changed' && message?.collection === 'stream-room-messages') {
          const roomMessage = message.fields.args[0]
          this.messages = this.messages.filter(Boolean)
          if (roomMessage.u.username === this.currentUser?.username) {
            if (this.lastMessage.distributed) {
              this.addMessage(roomMessage)
            } else {
              this.setMessageAsSent(roomMessage)
            }
          } else {
            this.addMessage(roomMessage)
          }
          this.setRoomAsRead(this.roomId)
        }
      })
    },

    subscribeTyping (roomId) {
      this.subscribeEvent('stream-notify-room', 'stream-notify-room', [`${roomId}/typing`, false], (message) => {
        if (message.msg === 'changed' && message?.collection === 'stream-notify-room') {
          const name = message.fields.args[0]
          if (this.currentUser?.name !== name) {
            const typing = message.fields.args[1]
            this.isTyping = typing
            setTimeout(() => {
              this.isTyping = false
            }, 2000)
          }
        }
      })
    },

    // no api dock exists for this one
    sendTypingEvent () {
      this.realTimeAPI.sendMessage({ msg: 'method', id: '24', method: 'stream-notify-room', params: [`${this.roomId}/user-activity`, this.currentUser?.name, ['user-typing']] })
    },

    addMessage (roomMessage) {
      const msgAdded = this.messages.some(msg => msg._id === roomMessage._id)
      if (!msgAdded) {
        this.messages = [...this.messages, this.formatMessage(roomMessage)]
        this.rocketMessages = [...this.rocketMessages, roomMessage]
      } else {
        const messageIndex = this.findMessageIndexById(roomMessage._id)
        if (messageIndex >= 0) {
          this.messages[messageIndex].reactions = this.formatReactions(roomMessage)
          this.messages[messageIndex].content = roomMessage.msg
          this.messages[messageIndex].files = this.formatAttachments(roomMessage)
        }
      }
    },

    findMessageIndexById (roomMessageId) {
      let messageIndex = null
      this.messages.forEach((message, index) => {
        if (message && message._id === roomMessageId) {
          messageIndex = index
        }
      })
      return messageIndex
    },

    setMessageAsSent (roomMessage) {
      // update last message for preview
      if (this.selectedRoom?.roomType === 'c') {
        this.channels.forEach((channel, index) => {
          if (channel.id === this.roomId) {
            const params = { lastMessage: roomMessage }
            this.updateLastChannelMessage({ params, index })
          }
        })
      } else if (this.selectedRoom?.roomType === 'p') {
        this.groups.forEach((group, index) => {
          if (group.id === this.roomId) {
            const params = { lastMessage: roomMessage }
            this.updateLastGroupMessage({ params, index })
          }
        })
      } else if (this.selectedRoom?.roomType === 'd') {
        this.smsConversations.forEach((smsConversation, index) => {
          if (smsConversation.id === this.roomId) {
            const params = { lastMessage: roomMessage }
            this.updateLastSmsConversationMessage({ params, index })
          }
        })
        this.directConversations.forEach((directConversation, index) => {
          if (directConversation.id === this.roomId) {
            const params = { lastMessage: roomMessage }
            this.updateLastDirectConversationMessage({ params, index })
          }
        })
      }
      Object.assign(this.lastMessage, {}, this.formatMessage(roomMessage))
    },

    sendChatMessage (roomId, msg, replyMessage) {
      let params
      if (replyMessage) {
        params = { rid: roomId, msg: msg, tmid: replyMessage._id }
      } else {
        params = { rid: roomId, msg: msg }
      }
      this.realTimeAPI.sendMessage({ msg: 'method', id: 'sendMessage', method: 'sendMessage', params: [params] })
    },

    // https://developer.rocket.chat/reference/api/realtime-api/method-calls/set-reaction
    sendMessageReaction (reaction, messageId, remove) {
      const emojiCode = emoji.find(reaction.unicode)?.key
      const params = [`:${emojiCode}:`, messageId, !remove]
      this.realTimeAPI.sendMessage({ msg: 'method', id: '31', method: 'setReaction', params: params })
    },

    rapiUpdateMessage (rocketMessage) {
      const params = [rocketMessage]
      this.realTimeAPI.sendMessage({ msg: 'method', id: '42', method: 'updateMessage', params: params })
    },

    rapiDeleteMessage (messageId) {
      const params = [{ _id: messageId }]
      this.realTimeAPI.sendMessage({ msg: 'method', id: 'deleteMessage', method: 'deleteMessage', params: params })
    },

    getMessageAvatar (roomMessage) {
      if (this.currentUser?.username === roomMessage.u.username) {
        return this.currentPanelUser?.pictureProfileUrl || this.currentUser?.pictureProfile
      } else if (this.selectedRoom?.avatar) {
        return this.selectedRoom?.avatar
      } else {
        return `${this.apiUrl}/avatar/${roomMessage.u.username}`
      }
    },

    getRoomType (type) {
      if (type === 'c') {
        return 'chat.publicChannels'
      } else if (type === 'p') {
        return 'chat.privateChannels'
      } else {
        return null
      }
    },

    beforeUnload () {
      this.realTimeAPI.disconnect()
    }
  }
}
